import { format, endOfMonth, endOfYear, startOfMonth, startOfYear } from 'date-fns';
import { DateRangeValueToLabelMap } from 'Utils';
import { DATE_RANGES_TYPES, FOCUS_VIEW } from 'Common/enums';
import * as _ from 'lodash';
import { Query, Filter, BinaryFilter } from '@cubejs-client/core';
import { shouldShowReviewConclusions } from 'Selectors';

const convertToCubeDateFormat = (date: Date) => {
    return format(date, 'yyyy-MM-dd');
};

const formatName = (merchant: string, dateRangeLabel: string) => {
    const today = format(new Date(), 'MMddyyyy');
    return `${merchant}_${dateRangeLabel}_${today}`;
};

export const MerchantReportTypeValue = {
    allMerchants: 'All Merchants',
    singleMerchant: 'Single Merchant'
};

export const generateTransactionValVolQuery = (
    chartType: Top5MerchantChartType,
    cubeQueryParts: CubeQueryParts,
    filters: Filter[]
): Query => {
    return {
        measures: [
            `MerchantSummary.${chartType}AllTransactions`,
            `MerchantSummary.${chartType}${FOCUS_VIEW.ACCEPT}Transactions`,
            `MerchantSummary.${chartType}${FOCUS_VIEW.REJECT}Transactions`,
            ...(shouldShowReviewConclusions() ? [`MerchantSummary.${chartType}${FOCUS_VIEW.REVIEW}Transactions`] : [])
        ],
        timeDimensions: [
            {
                dimension: 'MerchantSummary.requestTime',
                dateRange: cubeQueryParts?.dateRange
            }
        ],
        order: {
            'MerchantSummary.requestTime': 'asc'
        },
        filters
    };
};

export const generateTop5ChartQuery = (
    chartType: Top5MerchantChartType,
    focusView: AcceptRejectReview,
    cubeQueryParts: CubeQueryParts,
    filters: Filter[]
): Query => {
    const capitalizedChartType = _.capitalize(chartType);
    return {
        measures: [
            `MerchantSummary.${chartType}${focusView}Transactions`,
            `MerchantSummary.total${capitalizedChartType}Percent${focusView}`
        ],
        order: [[`MerchantSummary.total${capitalizedChartType}Percent${focusView}`, 'desc']],
        dimensions: ['MerchantSummary.mid'],
        filters,
        timeDimensions: [
            {
                dimension: 'MerchantSummary.requestTime',
                dateRange: cubeQueryParts?.dateRange
            }
        ],
        renewQuery: false,
        limit: 5
    };
};

export const generateRulesQuery = (cubeQueryParts: CubeQueryParts, filters: Filter[]): Query => {
    return {
        measures: ['Performance.matches'],
        order: [['Performance.matches', 'desc']],
        dimensions: ['Performance.rule'],
        filters,
        timeDimensions: [
            {
                dimension: 'Performance.requestTime',
                dateRange: cubeQueryParts?.dateRange
            }
        ],
        renewQuery: false,
        limit: 1000
    };
};

export const generateMerchantFilters = (afterDate: string, singleMerchantId?: string): Filter[] => {
    let filters: any = [];
    if (singleMerchantId) {
        filters = [
            {
                member: 'MerchantSummary.mid',
                operator: 'equals',
                values: [singleMerchantId]
            },
            {
                member: 'MerchantSummary.requestTime',
                operator: 'afterDate',
                values: [afterDate]
            }
        ];
    } else {
        filters = [
            {
                member: 'MerchantSummary.requestTime',
                operator: 'afterDate',
                values: [afterDate]
            }
        ];
    }
    return filters;
};

export const generateRulePerformanceFilters = (afterDate: string, singleMerchantId?: string): Filter[] => {
    const performanceRequestTimeObj = {
        member: 'Performance.requestTime',
        operator: 'afterDate',
        values: [afterDate]
    };
    const performanceReviewObj = {
        member: 'Performance.recommendation',
        operator: 'notEquals',
        values: ['REVIEW']
    };
    const performanceMidObj = {
        member: 'Performance.mid',
        operator: 'equals',
        values: [singleMerchantId!]
    };

    if (singleMerchantId) {
        return [
            performanceMidObj,
            performanceRequestTimeObj,
            ...(shouldShowReviewConclusions() ? [] : [performanceReviewObj])
        ] as BinaryFilter[];
    }
    return [
        performanceRequestTimeObj,
        ...(shouldShowReviewConclusions() ? [] : [performanceReviewObj])
    ] as BinaryFilter[];
};

export const generateRejectRulePerformanceFilters = (afterDate: string, singleMerchantId?: string): Filter[] => {
    let filters: any = [];
    if (singleMerchantId) {
        filters = [
            {
                member: 'Performance.mid',
                operator: 'equals',
                values: [singleMerchantId]
            },
            {
                member: 'Performance.requestTime',
                operator: 'afterDate',
                values: [afterDate]
            },
            {
                member: 'Performance.recommendation',
                operator: 'equals',
                values: ['REJECT']
            }
        ];
    } else {
        filters = [
            {
                member: 'Performance.requestTime',
                operator: 'afterDate',
                values: [afterDate]
            },
            {
                member: 'Performance.recommendation',
                operator: 'equals',
                values: ['REJECT']
            }
        ];
    }
    return filters;
};

export const reportCountQuery = (cubeQueryParts: CubeQueryParts, filters: Filter[]) => {
    const countQuery: Query = {
        measures: [
            'Performance.uniqueTransactionCount',
            'Performance.uniqueRejectedCount',
            'Performance.uniqueAcceptedCount',
            'Performance.uniqueAcceptedByRulesCount',
            'Performance.uniqueRejectedByRulesCount'
        ],
        timeDimensions: [
            {
                dimension: 'Performance.requestTime',
                dateRange: cubeQueryParts?.dateRange
            }
        ],
        filters
    };

    return {
        countQuery
    };
};

export const getCubeReportQuery = (formValues: ReportDataCount) => {
    const { dateRangeMinValue } = formValues.dateRange; // 2023-01-20T08:00:00.000Z
    // 2023-01-20T00:00:00.000Z
    const dateRangeMinStartTime = new Date(new Date(dateRangeMinValue).setHours(0, 0, 0, 0)).toISOString();
    const dateRangeMinTrimmed = dateRangeMinStartTime.split('T')[0]; // 2023-01-20
    const filters = generateRulePerformanceFilters(dateRangeMinTrimmed, formValues.singleMerchantId);
    const { countQuery } = reportCountQuery(formValues.cubeQueryParts, filters);
    return countQuery;
};

export const processFormValues = (
    values: NewMerchantReportFormValues,
    reportType: ReportType,
    t: (string: string) => string
) => {
    const cubeQueryParts: CubeQueryParts = {
        dateRange: ''
    };

    const merchant =
        values.reportType === MerchantReportTypeValue.singleMerchant
            ? `MID ${values.singleMerchantId}`
            : 'All Merchants';
    let dateRangeLabel = '';
    let name = '';
    const { dateRangeType, dateRangeValue, dateRangeMinValue } = values?.dateRange;

    switch (dateRangeType) {
        case DATE_RANGES_TYPES.CUSTOM: {
            const { startDate, endDate } = dateRangeValue as CustomDateRange;
            const convertedStartDate = convertToCubeDateFormat(startDate);
            const convertedEndDate = convertToCubeDateFormat(endDate);
            cubeQueryParts.dateRange = [convertedStartDate, convertedEndDate];
            dateRangeLabel = `${format(startDate, 'MM/dd/yyyy')} - ${format(endDate, 'MM/dd/yyyy')}`;
            const nameDateRangeLabel = `${format(startDate, 'MMM dd yyyy')}-${format(endDate, 'MMM dd yyyy')}`;
            name = formatName(merchant, nameDateRangeLabel);
            break;
        }
        case DATE_RANGES_TYPES.PAST_DAYS: {
            cubeQueryParts.dateRange = dateRangeValue as string;
            dateRangeLabel = t(DateRangeValueToLabelMap[dateRangeType]?.[dateRangeValue as string]);
            name = formatName(merchant, dateRangeLabel);
            break;
        }
        case DATE_RANGES_TYPES.MONTH: {
            const { month, year } = dateRangeValue as MonthDateRangeTypeValue;
            const selectedMonth = new Date(year, month);
            cubeQueryParts.dateRange = [
                convertToCubeDateFormat(selectedMonth),
                convertToCubeDateFormat(endOfMonth(selectedMonth))
            ];
            dateRangeLabel = `${t(DateRangeValueToLabelMap[dateRangeType]?.[month])} ${year}`;
            name = formatName(merchant, dateRangeLabel);
            break;
        }
        case DATE_RANGES_TYPES.YEAR: {
            const selectedYear = new Date(dateRangeValue as number, 0, 1);
            cubeQueryParts.dateRange = [
                convertToCubeDateFormat(selectedYear),
                convertToCubeDateFormat(endOfYear(selectedYear))
            ];
            dateRangeLabel = String(dateRangeValue);
            name = formatName(merchant, dateRangeLabel);
            break;
        }
        case DATE_RANGES_TYPES.MONTH_TO_DATE: {
            const today = new Date();
            cubeQueryParts.dateRange = [convertToCubeDateFormat(startOfMonth(today)), convertToCubeDateFormat(today)];
            dateRangeLabel = t(dateRangeType);
            name = formatName(merchant, dateRangeLabel);
            break;
        }
        case DATE_RANGES_TYPES.YEAR_TO_DATE: {
            const today = new Date();
            cubeQueryParts.dateRange = [convertToCubeDateFormat(startOfYear(today)), convertToCubeDateFormat(today)];
            dateRangeLabel = t(dateRangeType);
            name = formatName(merchant, dateRangeLabel);
            break;
        }
        default: {
            break;
        }
    }

    const afterDate = convertToCubeDateFormat(dateRangeMinValue);

    const merchantFilters = generateMerchantFilters(afterDate, values?.singleMerchantId);
    let fullQueries: any;

    switch (reportType) {
        case 'merchantsummary':
            fullQueries = {
                transactionValueQuery: generateTransactionValVolQuery('value', cubeQueryParts, merchantFilters),
                transactionVolumeQuery: generateTransactionValVolQuery('volume', cubeQueryParts, merchantFilters)
            } as MerchantReportFullQueries;

            if (values.reportType === MerchantReportTypeValue.allMerchants) {
                fullQueries.top5Value = {
                    Accept: generateTop5ChartQuery(
                        'value',
                        FOCUS_VIEW.ACCEPT as AcceptRejectReview,
                        cubeQueryParts,
                        merchantFilters
                    ),
                    Reject: generateTop5ChartQuery(
                        'value',
                        FOCUS_VIEW.REJECT as AcceptRejectReview,
                        cubeQueryParts,
                        merchantFilters
                    ),
                    ...(shouldShowReviewConclusions()
                        ? {
                              Review: generateTop5ChartQuery(
                                  'value',
                                  FOCUS_VIEW.REVIEW as AcceptRejectReview,
                                  cubeQueryParts,
                                  merchantFilters
                              )
                          }
                        : {})
                };
                fullQueries.top5Volume = {
                    Accept: generateTop5ChartQuery(
                        'volume',
                        FOCUS_VIEW.ACCEPT as AcceptRejectReview,
                        cubeQueryParts,
                        merchantFilters
                    ),
                    Reject: generateTop5ChartQuery(
                        'volume',
                        FOCUS_VIEW.REJECT as AcceptRejectReview,
                        cubeQueryParts,
                        merchantFilters
                    ),
                    ...(shouldShowReviewConclusions()
                        ? {
                              Review: generateTop5ChartQuery(
                                  'volume',
                                  FOCUS_VIEW.REVIEW as AcceptRejectReview,
                                  cubeQueryParts,
                                  merchantFilters
                              )
                          }
                        : {})
                };
            }
            cubeQueryParts.filters = merchantFilters;
            break;
        case 'performance':
            fullQueries = {
                rejectRuleQuery: generateRulesQuery(
                    cubeQueryParts,
                    generateRejectRulePerformanceFilters(afterDate, values?.singleMerchantId)
                ),
                allRuleQuery: generateRulesQuery(
                    cubeQueryParts,
                    generateRulePerformanceFilters(afterDate, values?.singleMerchantId)
                )
            } as RulePerformanceReportFullQueries;
            cubeQueryParts.filters = generateRulePerformanceFilters(afterDate, values?.singleMerchantId);
            break;
        default:
            break;
    }

    return {
        ...values,
        merchant,
        name,
        dateRangeLabel,
        cubeQueryParts,
        fullQueries
    };
};
